/* tslint:disable */
import {
  Attributes,
  Transformer,
  IOptions,
  SanitizeResponse,
  Issues,
  IFrame,
  TagPosition,
} from '@/shared/util/sanitize-html/types';

import htmlparser, { ParserOptions } from 'htmlparser2';

import quoteRegexp from 'lodash/escapeRegExp';
import cloneDeep from 'lodash/cloneDeep';
import mergeWith from 'lodash/mergeWith';
import isString from 'lodash/isString';
import isPlainObject from 'lodash/isPlainObject';

const srcset = require('srcset');
const postcss = require('postcss');

const LINE_FEED = 10; // = /n
const CARRIAGE_RETURN = 13; // = /r

function each(obj: any, cb: any) {
  if (obj) {
    Object.keys(obj).forEach((key: string) => {
      cb(obj[key], key);
    });
  }
}

// Avoid false positives with .__proto__, .hasOwnProperty, etc.
function has(obj: any, key: any) {
  return {}.hasOwnProperty.call(obj, key);
}

// Returns those elements of `a` for which `cb(a)` returns truthy
function filter(a: any, cb: any) {
  const n: any[] = [];
  each(a, (v: any) => {
    if (cb(v)) {
      n.push(v);
    }
  });
  return n;
}

// A valid attribute name.
// We use a tolerant definition based on the set of strings defined by
// html.spec.whatwg.org/multipage/parsing.html#before-attribute-name-state
// and html.spec.whatwg.org/multipage/parsing.html#attribute-name-state .
// The characters accepted are ones which can be appended to the attribute
// name buffer without triggering a parse error:
//   * unexpected-equals-sign-before-attribute-name
//   * unexpected-null-character
//   * unexpected-character-in-attribute-name
// We exclude the empty string because it's impossible to get to the after
// attribute name state with an empty attribute name buffer.
const VALID_HTML_ATTRIBUTE_NAME = /^[^\0\t\n\f\r /<=>]+$/;

// Ignore the _recursing flag; it's there for recursive
// invocation as a guard against this exploit:
// https://github.com/fb55/htmlparser2/issues/105

export function sanitizeHtml(html: string, options: IOptions): SanitizeResponse {
  let result = '';
  const issues: Issues[] = [];

  function Frame(tag: string, attribs?: { [index: string]: string }): IFrame {
    return {
      tag,
      attribs: attribs || {},
      tagPosition: result.length,
      text: '',
      updateParentNodeText: (thisFrame: IFrame) => {
        if (stack.length) {
          const parentFrame = stack[stack.length - 1];
          parentFrame.text += thisFrame.text;
        }
      },
    };
  }

  function addIssue(parser: htmlparser.Parser, description: string): void {
    const tagPosition: TagPosition = getTagPosition(parser);
    issues.push({ column: tagPosition.column, line: tagPosition.row, description });
  }

  function getTagPosition(parser: htmlparser.Parser): TagPosition {
    const characterIndex: number = (parser as any).startIndex;
    let row = 1;
    let column = 1;
    let previousCharacter = null;
    for (let i = 0; i <= characterIndex; i++) {
      const character = html.charCodeAt(i);
      if (character === CARRIAGE_RETURN || (previousCharacter !== CARRIAGE_RETURN && character === LINE_FEED)) {
        row++;
        column = 0;
      } else {
        column++;
      }
      previousCharacter = character;
    }

    return { row, column };
  }

  if (!options) {
    options = sanitizeHtml.defaults;
    options.parser = htmlParserDefaults;
  } else {
    options = Object.assign({}, sanitizeHtml.defaults, options);
    if (options.parser) {
      options.parser = Object.assign({}, htmlParserDefaults, options.parser);
    } else {
      options.parser = htmlParserDefaults;
    }
  }

  // Tags that contain something other than HTML, or where discarding
  // the text when the tag is disallowed makes sense for other reasons.
  // If we are not allowing these tags, we should drop their content too.
  // For other tags you would drop the tag but keep its content.
  const nonTextTagsArray = options.nonTextTags || ['script', 'style', 'textarea'];
  let allowedAttributesMap: any;
  let allowedAttributesGlobMap: any;

  if (options.allowedAttributes) {
    allowedAttributesMap = {};
    allowedAttributesGlobMap = {};
    each(options.allowedAttributes, (attributes: any, tag: any) => {
      allowedAttributesMap[tag] = [];
      const globRegex: any[] = [];
      attributes.forEach((obj: any) => {
        if (isString(obj) && obj.indexOf('*') >= 0) {
          globRegex.push(quoteRegexp(obj).replace(/\\\*/g, '.*'));
        } else {
          allowedAttributesMap[tag].push(obj);
        }
      });
      allowedAttributesGlobMap[tag] = new RegExp('^(' + globRegex.join('|') + ')$');
    });
  }
  const allowedClassesMap: any = {};
  each(options.allowedClasses, (classes: any, tag: any) => {
    // Implicitly allows the class attribute
    if (allowedAttributesMap) {
      if (!has(allowedAttributesMap, tag)) {
        allowedAttributesMap[tag] = [];
      }
      allowedAttributesMap[tag].push('class');
    }

    allowedClassesMap[tag] = classes;
  });

  const transformTagsMap: any = {};
  let transformTagsAll: any;
  each(options.transformTags, (transform: any, tag: any) => {
    let transFun;
    if (typeof transform === 'function') {
      transFun = transform;
    } else if (typeof transform === 'string') {
      transFun = simpleTransform(transform);
    }
    if (tag === '*') {
      transformTagsAll = transFun;
    } else {
      transformTagsMap[tag] = transFun;
    }
  });

  let depth = 0;
  const stack: any[] = [];
  const skipMap: any = {};
  const transformMap: any = {};
  let skipText = false;
  let skipTextDepth = 0;

  const parser = new htmlparser.Parser(
    {
      onopentag: (name: string, attribs: Attributes) => {
        if (skipText) {
          skipTextDepth++;
          return;
        }
        const frame: IFrame = Frame(name, attribs);
        stack.push(frame);

        let skip = false;
        const hasText = !!frame.text;
        let transformedTag;
        if (has(transformTagsMap, name)) {
          transformedTag = transformTagsMap[name](name, attribs);

          frame.attribs = attribs = transformedTag.attribs;

          if (transformedTag.text !== undefined) {
            frame.innerText = transformedTag.text;
          }

          if (name !== transformedTag.tagName) {
            frame.name = name = transformedTag.tagName;
            transformMap[depth] = transformedTag.tagName;
          }
        }
        if (transformTagsAll) {
          transformedTag = transformTagsAll(name, attribs);

          frame.attribs = attribs = transformedTag.attribs;
          if (name !== transformedTag.tagName) {
            frame.name = name = transformedTag.tagName;
            transformMap[depth] = transformedTag.tagName;
          }
        }

        if (options.allowedTags && (options.allowedTags as string[]).indexOf(name) === -1) {
          skip = true;
          if (nonTextTagsArray.indexOf(name) !== -1) {
            skipText = true;
            skipTextDepth = 1;
          }
          skipMap[depth] = true;
        }
        depth++;
        if (skip) {
          addIssue(parser, `Tag '${name}' not allowed`);
          // We want the contents but not this tag
          return;
        }
        result += '<' + name;
        if (!allowedAttributesMap || has(allowedAttributesMap, name) || allowedAttributesMap['*']) {
          const removedAttributes: string[] = [];
          each(attribs, (value: any, a: any) => {
            if (!VALID_HTML_ATTRIBUTE_NAME.test(a)) {
              // This prevents part of an attribute name in the output from being
              // interpreted as the end of an attribute, or end of a tag.
              removedAttributes.push(a);
              delete frame.attribs[a];
              return;
            }
            let parsed: any;
            // check allowedAttributesMap for the element and attribute and modify the value
            // as necessary if there are specific values defined.
            let passedAllowedAttributesMapCheck = false;
            if (
              !allowedAttributesMap ||
              (has(allowedAttributesMap, name) && allowedAttributesMap[name].indexOf(a) !== -1) ||
              (allowedAttributesMap['*'] && allowedAttributesMap['*'].indexOf(a) !== -1) ||
              (has(allowedAttributesGlobMap, name) && allowedAttributesGlobMap[name].test(a)) ||
              (allowedAttributesGlobMap['*'] && allowedAttributesGlobMap['*'].test(a))
            ) {
              passedAllowedAttributesMapCheck = true;
            } else if (allowedAttributesMap && allowedAttributesMap[name]) {
              for (const o of allowedAttributesMap[name]) {
                if (isPlainObject(o) && o.name && o.name === a) {
                  passedAllowedAttributesMapCheck = true;
                  let newValue = '';
                  if (o.multiple === true) {
                    // verify the values that are allowed
                    const splitStrArray = value.split(' ');
                    for (const s of splitStrArray) {
                      if (o.values.indexOf(s) !== -1) {
                        if (newValue === '') {
                          newValue = s;
                        } else {
                          newValue += ' ' + s;
                        }
                      }
                    }
                  } else if (o.values.indexOf(value) >= 0) {
                    // verified an allowed value matches the entire attribute value
                    newValue = value;
                  }
                  value = newValue;
                }
              }
            }
            if (passedAllowedAttributesMapCheck) {
              if ((options.allowedSchemesAppliedToAttributes as string[]).indexOf(a) !== -1) {
                if (naughtyHref(name, value)) {
                  addIssue(parser, `Invalid href '${value}'`);
                  delete frame.attribs[a];
                  return;
                }
              }
              if (name === 'iframe' && a === 'src') {
                let allowed: boolean = true;
                try {
                  // naughtyHref is in charge of whether protocol relative URLs
                  // are cool. We should just accept them
                  parsed = parseUrl(value);
                  const isRelativeUrl = parsed && parsed.host === null && parsed.protocol === null;
                  if (isRelativeUrl) {
                    // default value of allowIframeRelativeUrls is true unless allowIframeHostnames specified
                    allowed = !!(has(options, 'allowIframeRelativeUrls')
                      ? options.allowIframeRelativeUrls
                      : !options.allowedIframeHostnames);
                  } else if (options.allowedIframeHostnames) {
                    allowed = !!options.allowedIframeHostnames.find((hostname) => hostname === parsed.hostname);
                  }
                } catch (e) {
                  // Unparseable iframe src
                  allowed = false;
                }
                if (!allowed) {
                  addIssue(parser, `Invalid iframe src '${value}'`);
                  delete frame.attribs[a];
                  return;
                }
              }
              if (a === 'srcset') {
                try {
                  parsed = srcset.parse(value);
                  each(parsed, (value: any) => {
                    if (naughtyHref('srcset', value.url)) {
                      value.evil = true;
                    }
                  });
                  parsed = filter(parsed, (v: any) => !v.evil);
                  if (!parsed.length) {
                    removedAttributes.push(a);
                    delete frame.attribs[a];
                    return;
                  } else {
                    value = srcset.stringify(filter(parsed, (v: any) => !v.evil));
                    frame.attribs[a] = value;
                  }
                } catch (e) {
                  // Unparseable srcset
                  removedAttributes.push(a);
                  delete frame.attribs[a];
                  return;
                }
              }
              if (a === 'class') {
                value = filterClasses(value, allowedClassesMap[name]);
                if (!value.length) {
                  delete frame.attribs[a];
                  return;
                }
              }
              if (a === 'style') {
                try {
                  const abstractSyntaxTree = postcss.parse(name + ' {' + value + '}');
                  const filteredAST = filterCss(abstractSyntaxTree, options.allowedStyles);

                  value = stringifyStyleAttributes(filteredAST);

                  if (value.length === 0) {
                    addIssue(parser, `Invalid CSS styles`);
                    delete frame.attribs[a];
                    return;
                  }
                } catch (e) {
                  addIssue(parser, `Invalid CSS styles`);
                  delete frame.attribs[a];
                  return;
                }
              }
              result += ' ' + a;
              if (value.length) {
                result += '="' + escapeHtml(value, true) + '"';
              }
            } else {
              removedAttributes.push(a);
              delete frame.attribs[a];
            }
          });
          if (removedAttributes.length > 0) {
            addIssue(parser, `Attributes not allow for tag '${name}' : [${removedAttributes.join(', ')}]`);
          }
        }
        if ((options.selfClosing as string[]).indexOf(name) !== -1) {
          result += ' />';
        } else {
          result += '>';
          if (frame.innerText && !hasText && !options.textFilter) {
            result += frame.innerText;
          }
        }
      },
      ontext: (text) => {
        if (skipText) {
          return;
        }
        const lastFrame = stack[stack.length - 1];
        let tag;

        if (lastFrame) {
          tag = lastFrame.tag;
          // If inner text was set by transform function then let's use it
          text = lastFrame.innerText !== undefined ? lastFrame.innerText : text;
        }

        if (tag === 'script' || tag === 'style') {
          // htmlparser2 gives us these as-is. Escaping them ruins the content. Allowing
          // script tags is, by definition, game over for XSS protection, so if that's
          // your concern, don't allow them. The same is essentially true for style tags
          // which have their own collection of XSS vectors.
          result += text;
        } else {
          const escaped = escapeHtml(text, false);
          if (options.textFilter) {
            result += options.textFilter(escaped);
          } else {
            result += escaped;
          }
        }
        if (stack.length) {
          const frame = stack[stack.length - 1];
          frame.text += text;
        }
      },
      /*onclosetag: (name: string) => {*/
      onclosetag: () => {
        const lastFrame = stack[stack.length - 1];
        let tag;

        if (lastFrame) {
          tag = lastFrame.tag;
        }

        if (skipText) {
          skipTextDepth--;
          if (!skipTextDepth) {
            skipText = false;
          } else {
            return;
          }
        }

        const frame = stack.pop();
        if (!frame) {
          // Do not crash on bad markup
          return;
        }
        skipText = false;
        depth--;
        if (skipMap[depth]) {
          delete skipMap[depth];
          frame.updateParentNodeText(frame);
          return;
        }

        if (transformMap[depth]) {
          tag = transformMap[depth];
          delete transformMap[depth];
        }

        if (options.exclusiveFilter && options.exclusiveFilter(frame)) {
          result = result.substr(0, frame.tagPosition);
          return;
        }

        frame.updateParentNodeText(frame);

        if ((options.selfClosing as string[]).indexOf(tag) !== -1) {
          // Already output />
          return;
        }

        result += '</' + tag + '>';
      },
    },
    options.parser,
  );

  parser.write(html);
  parser.end();

  return {
    input: html,
    output: result,
    issues,
  };

  function escapeHtml(s: any, quote: any) {
    if (typeof s !== 'string') {
      s = s + '';
    }
    if ((options.parser as ParserOptions).decodeEntities) {
      s = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\>/g, '&gt;');
      if (quote) {
        s = s.replace(/\"/g, '&quot;');
      }
    }
    // TODO: this is inadequate because it will pass `&0;`. This approach
    // will not work, each & must be considered with regard to whether it
    // is followed by a 100% syntactically valid entity or not, and escaped
    // if it is not. If this bothers you, don't set parser.decodeEntities
    // to false. (The default is true.)
    s = s
      .replace(/&(?![a-zA-Z0-9#]{1,20};)/g, '&amp;') // Match ampersands not part of existing HTML entity
      .replace(/</g, '&lt;')
      .replace(/\>/g, '&gt;');
    if (quote) {
      s = s.replace(/\"/g, '&quot;');
    }
    return s;
  }

  function naughtyHref(name: any, href: any) {
    // Browsers ignore character codes of 32 (space) and below in a surprising
    // number of situations. Start reading here:
    // https://www.owasp.org/index.php/XSS_Filter_Evasion_Cheat_Sheet#Embedded_tab
    href = href.replace(/[\x00-\x20]+/g, '');
    // Clobber any comments in URLs, which the browser might
    // interpret inside an XML data island, allowing
    // a javascript: URL to be snuck through
    href = href.replace(/<\!\-\-.*?\-\-\>/g, '');
    // Case insensitive so we don't get faked out by JAVASCRIPT #1
    const matches = href.match(/^([a-zA-Z]+)\:/);
    if (!matches) {
      // Protocol-relative URL starting with any combination of '/' and '\'
      if (href.match(/^[\/\\]{2}/)) {
        return !options.allowProtocolRelative;
      }

      // No scheme
      return false;
    }
    const scheme = matches[1].toLowerCase();

    if (has(options.allowedSchemesByTag, name)) {
      return (options as any).allowedSchemesByTag[name].indexOf(scheme) === -1;
    }

    return !options.allowedSchemes || (options as any).allowedSchemes.indexOf(scheme) === -1;
  }

  /**
   * Filters user input css properties by whitelisted regex attributes.
   *
   * @param {object} abstractSyntaxTree  - Object representation of CSS attributes.
   * @property {array[Declaration]} abstractSyntaxTree.nodes[0] - Each object cointains prop and value key, i.e { prop: 'color', value: 'red' }.
   * @param {object} allowedStyles       - Keys are properties (i.e color), value is list of permitted regex rules (i.e /green/i).
   * @return {object}                    - Abstract Syntax Tree with filtered style attributes.
   */
  function filterCss(abstractSyntaxTree: any, allowedStyles: any) {
    if (!allowedStyles) {
      return abstractSyntaxTree;
    }

    const filteredAST = cloneDeep(abstractSyntaxTree);
    const astRules = abstractSyntaxTree.nodes[0];
    let selectedRule;

    // Merge global and tag-specific styles into new AST.
    if (allowedStyles[astRules.selector] && allowedStyles['*']) {
      selectedRule = mergeWith(
        cloneDeep(allowedStyles[astRules.selector]),
        allowedStyles['*'],
        (objValue: any, srcValue: any) => {
          if (Array.isArray(objValue)) {
            return objValue.concat(srcValue);
          }
        },
      );
    } else {
      selectedRule = allowedStyles[astRules.selector] || allowedStyles['*'];
    }

    if (selectedRule) {
      filteredAST.nodes[0].nodes = astRules.nodes.reduce(filterDeclarations(selectedRule), []);
    }

    return filteredAST;
  }

  /**
   * Extracts the style attribues from an AbstractSyntaxTree and formats those
   * values in the inline style attribute format.
   *
   * @param  {AbstractSyntaxTree} filteredAST
   * @return {string}             - Example: "color:yellow;text-align:center;font-family:helvetica;"
   */
  function stringifyStyleAttributes(filteredAST: any) {
    return filteredAST.nodes[0].nodes
      .reduce((extractedAttributes: any, attributeObject: any) => {
        extractedAttributes.push(attributeObject.prop + ':' + attributeObject.value);
        return extractedAttributes;
      }, [])
      .join(';');
  }

  /**
   * Filters the existing attributes for the given property. Discards any attributes
   * which don't match the whitelist.
   *
   * @param  {object} selectedRule             - Example: { color: red, font-family: helvetica }
   * @param  {array} allowedDeclarationsList   - List of declarations which pass whitelisting.
   * @param  {object} attributeObject          - Object representing the current css property.
   * @property {string} attributeObject.type   - Typically 'declaration'.
   * @property {string} attributeObject.prop   - The CSS property, i.e 'color'.
   * @property {string} attributeObject.value  - The corresponding value to the css property, i.e 'red'.
   * @return {function}                        - When used in Array.reduce, will return an array of Declaration objects
   */
  function filterDeclarations(selectedRule: any) {
    return (allowedDeclarationsList: any, attributeObject: any) => {
      // If this property is whitelisted...
      if (selectedRule.hasOwnProperty(attributeObject.prop)) {
        const matchesRegex = selectedRule[attributeObject.prop].some((regularExpression: any) =>
          regularExpression.test(attributeObject.value),
        );

        if (matchesRegex) {
          allowedDeclarationsList.push(attributeObject);
        }
      }
      return allowedDeclarationsList;
    };
  }

  function filterClasses(classes: any, allowed: any) {
    if (!allowed) {
      // The class attribute is allowed without filtering on this tag
      return classes;
    }
    classes = classes.split(/\s+/);
    return classes.filter((clss: any) => allowed.indexOf(clss) !== -1).join(' ');
  }
}

// Defaults are accessible to you so that you can use them as a starting point
// programmatically if you wish

const htmlParserDefaults = {
  decodeEntities: true,
};
sanitizeHtml.defaults = {
  allowedTags: [
    'h3',
    'h4',
    'h5',
    'h6',
    'blockquote',
    'p',
    'a',
    'ul',
    'ol',
    'nl',
    'li',
    'b',
    'i',
    'strong',
    'em',
    'strike',
    'code',
    'hr',
    'br',
    'div',
    'table',
    'thead',
    'caption',
    'tbody',
    'tr',
    'th',
    'td',
    'pre',
    'iframe',
  ],
  allowedAttributes: {
    a: ['href', 'name', 'target'],
    // We don't currently allow img itself by default, but this
    // would make sense if we did. You could add srcset here,
    // and if you do the URL is checked for safety
    img: ['src'],
  },
  // Lots of these won't come up by default because we don't allow them
  selfClosing: ['img', 'br', 'hr', 'area', 'base', 'basefont', 'input', 'link', 'meta'],
  // URL schemes we permit
  allowedSchemes: ['http', 'https', 'ftp', 'mailto'],
  allowedSchemesByTag: {},
  allowedSchemesAppliedToAttributes: ['href', 'src', 'cite'],
  allowProtocolRelative: true,
};

export function simpleTransform(newTagName: string, attribs?: Attributes, merge: boolean = true): Transformer {
  const newAttribs = attribs || {};

  return (tagName: string, attributes: Attributes) => {
    let attribute;
    if (merge) {
      for (attribute in newAttribs) {
        attributes[attribute] = newAttribs[attribute];
      }
    } else {
      attributes = newAttribs;
    }

    return {
      tagName: newTagName,
      attribs: attributes,
    };
  };
}

function parseUrl(value: string) {
  try {
    const urlObj = new URL(value);
    return {
      protocol: urlObj.protocol, // e.g., 'https:'
      host: urlObj.host, // e.g., 'example.com:8080' (includes port if present)
      hostname: urlObj.hostname, // e.g., 'example.com' (no port)
    };
  } catch (e) {
    // Handle invalid URLs (e.g., return null or throw an error)
    return null;
  }
}
